/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { ChangeEvent, useEffect, useMemo } from 'react'

import posthog from 'posthog-js'
import { useSearchParams } from 'react-router-dom'
import { useStopwatch } from 'react-timer-hook'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'

import { FaucetButton } from '@src/components/FaucetButton'
import MilesTracker from '@src/components/MilesTracker'
import TransactionStatusCard from '@src/components/TransactionStatusCard'
import ConnectButton from '@src/components/buttons/ConnectButton'
import { ChainId, NATIVE_TOKEN_ADDRESS, WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
import { DEFAULT_SELL_TOKEN } from '@src/configs/defaults'
import { LARGE_SCREEN_THRESHOLD } from '@src/constants/exchange'
import { LiquiditySource, ResultCode, StepNames } from '@src/enums'
import { useFetchAllBalances } from '@src/hooks/useFetchAllBalances'
import { useFetchBoundReferralCode } from '@src/hooks/useFetchBoundReferralCode'
import { useFetchGeneratedReferralCode } from '@src/hooks/useFetchGeneratedReferralCode'
import { usePrecomputedGasPrices } from '@src/hooks/usePrecomputedGasPrices'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { FromSection } from '@src/pages/Exchange/components/FromSection'
import { GasFeePanel } from '@src/pages/Exchange/components/GasFeePanel'
import RefreshButton from '@src/pages/Exchange/components/Refresh'
import { SwapActionButton } from '@src/pages/Exchange/components/SwapActionButton'
import SwapInProgressFlow from '@src/pages/Exchange/components/SwapInProgressFlow'
import { ToSection } from '@src/pages/Exchange/components/ToSection'
import { ToggleExchangeTokens } from '@src/pages/Exchange/components/ToggleExchangeTokens'
import { AllTooltips } from '@src/pages/Exchange/components/Tooltips'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import {
    useDestinationAddress,
    useExchangeAmountLogic,
    useExchangeDerivedState,
    useExchangeMemos,
    useExchangeToken,
    useGasEstimation,
    useOrderUpdates,
    useSwapContract,
    useToggleExchangeTokens,
} from '@src/pages/Exchange/hooks'
import { IExchangeData } from '@src/types/exchange'
import { TransactionType } from '@src/types/gasPrices'
import { IQuoteData } from '@src/types/order'
import { Address, IToken } from '@src/types/token'
import {
    Balance,
    createContextLogger,
    formatExchangeAmountWithCommas,
    formatNumberStringWithCommas,
    getAllowance,
    isFeatureEnabled,
    isUnWrapOnly,
    isWrapOnly,
    returnSanitizedSellAmount,
} from '@src/utils'
import { handleTokenSelection, isSameExchangeToken, validateUrlTokenParams } from '@src/utils/tokenValidation'
import { useApplicationStore, useConfigStore, useHistoryStore, useTokensStore } from '@src/zustand_stores'
import { useInvalidateBalances } from '@src/zustand_stores/tokens.store'

import useExchangeBalances from './hooks/useExchangeBalances'

const logger = createContextLogger('ExchangePage')

const ExchangePage = () => {
    const {
        isGasFeeExpanded,
        machFee,
        isReviewSwapModalOpen,
        isQuoteExpired,
        isManuallyRefreshing,
        isCustomWallet,
        hasInsufficientGas,
        isPriceFlipped,
        isToggleInProgress,
        sellAllowance,
        isQuoteEnabled,
        setIsGasFeeExpanded,
        setIsReviewSwapModalOpen,
        setIsCustomWallet,
        setIsPriceFlipped,
        setSellAllowance,
        setChainId,
    } = useExchangeState()

    const { isCrossChain, validateAddress, getAutoDetectedAddress, isCorrectWalletConnected, isWrapOrUnwrapOnly } =
        useExchangeDerivedState()

    const {
        exchangeId,
        orderId,
        currentModalId,
        setExchangeId,
        setCurrentModalId,
        setWaitingForConnectedWalletInteraction,
        refreshStatus,
    } = useApplicationStore()

    const swapContracts = useConfigStore(config => config.swapContracts)
    const getOrder = useHistoryStore(state => state.getOrder)
    const removeHistory = useHistoryStore(state => state.removeHistory)
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const exchanges = useHistoryStore(state => state.history)
    const transactionData = getOrder(exchangeId)

    const [searchParams] = useSearchParams()

    /**
     * * * * * * * * * * * * * * * * * * * * *  NEW ZUSTAND STORES * * * * * * * * * * * * * * *
     */
    const { isTronConnected, evmWalletAddress, tronWalletAddress, isSolanaConnected, solanaWalletAddress, EVMChainId } =
        useWalletConnections()

    const nativeTokenPricesInUSD = useTokensStore(state => state.nativeTokenPricesInUSD)
    const getGasTokenProps = useTokensStore(state => state.getGasTokenProps)

    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    /**
     * * * * * * * * * * * * * * * * * * * * * NEW EXCHANGE DATA CONTEXT * * * * * * * * * * * * * * *
     */
    const {
        updateSellData,
        updateBuyData,
        updateBothData,
        refetchQuote,
        sellData,
        buyData,
        mainWalletAddress,
        quoteResponse,
        isQuoteLoading,
    } = useExchangeDataContext()
    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    useFetchAllBalances() // This will automatically update token balances in the store

    useFetchBoundReferralCode() // This will automatically update bound referral code in application store

    useFetchGeneratedReferralCode() // This will automatically update generated referral code in application store

    const { nativeBalance, wrappedNativeBalance, refreshAllBalancesAndWait } = useExchangeBalances()

    // Add the invalidateBalances function
    const invalidateBalances = useInvalidateBalances()

    useEffect(() => {
        if (isTronConnected) {
            setChainId(ChainId.Tron)
        } else if (isSolanaConnected) {
            setChainId(ChainId.Solana)
        } else if (EVMChainId) {
            setChainId(EVMChainId)
        }
    }, [isTronConnected, isSolanaConnected, EVMChainId])

    // Websocket hook which runs in the background and only connects if there is a valid exchangeId + orderId
    useOrderUpdates(exchangeId, orderId)

    const { precomputedGasPrices, isComputing, isGasPrecomputingDone } = usePrecomputedGasPrices(swapContracts)

    const {
        sellAmountString,
        isInsufficientBalance,
        isBuyOutputLoading,
        isQuoteInvalid,
        liquiditySource,
        onChangeSellValue,
        handleManualRefresh,
        setMax,
        setSellAmountString,
    } = useExchangeAmountLogic({
        isComputing,
        isGasPrecomputingDone,
        precomputedGasPrices,
    })

    useEffect(() => {
        if (!isComputing && isGasPrecomputingDone) {
            logger.info('[ExchangePage useEffect] precomputedGasPrices:', {
                precomputedGasPrices,
            })
            if (precomputedGasPrices && nativeTokenPricesInUSD) {
                const computeUSDFees = async () => {
                    const precomputedGasPricesInUSD: Record<number, Record<string, any>> = {}
                    for (const chainId of Object.keys(precomputedGasPrices)) {
                        precomputedGasPricesInUSD[+chainId] = {}

                        // Iterate over the values:
                        for (const txType of Object.values(TransactionType)) {
                            if (+chainId === ChainId.Solana) {
                                /// TODO
                            }

                            if (+chainId === ChainId.Tron) {
                                /// TODO
                            }

                            const txTypeResults = precomputedGasPrices[+chainId].results[txType as TransactionType]

                            if (txTypeResults.totalGasUnits && txTypeResults.totalFeePerGasUnit) {
                                const totalGasUnits = txTypeResults.totalGasUnits
                                const totalFeePerGasUnit = txTypeResults.totalFeePerGasUnit

                                const totalGas = totalGasUnits * totalFeePerGasUnit

                                const gasInToken = new Balance(getGasTokenProps(+chainId))
                                    .setValue(BigInt(totalGas))
                                    .toNumber()
                                const gasInUSD = gasInToken * nativeTokenPricesInUSD[+chainId]

                                precomputedGasPricesInUSD[+chainId][txType] = {
                                    gasInToken: gasInToken,
                                    gasInUSD: gasInUSD,
                                }
                            } else {
                                precomputedGasPricesInUSD[+chainId][txType] = {}
                            }
                        }
                    }

                    logger.info('[ExchangePage useEffect] precomputedGasPricesInUSD:', {
                        precomputedGasPricesInUSD,
                    })
                }

                computeUSDFees()
            }
        }
    }, [precomputedGasPrices, isComputing, isGasPrecomputingDone, swapContracts, nativeTokenPricesInUSD])

    const { isCCTP, disabledChains, hasErrors, computedMilesAndMultiplier, shortenedAddress, feesDisplay } =
        useExchangeMemos({
            sellData,
            buyData,
            isInsufficientBalance,
            isQuoteInvalid,
            isBuyOutputLoading,
            isQuoteExpired,
            address: evmWalletAddress,
            solanaWalletAddress: solanaWalletAddress?.toString(),
            machFee,
        })
    const miles = computedMilesAndMultiplier.miles

    const { reset: resetStopwatch } = useStopwatch({ autoStart: false })

    const onCloseReviewSwapModal = () => {
        const isLargeScreen = window.innerWidth >= LARGE_SCREEN_THRESHOLD

        // Show a Toast if needed...
        if (
            exchanges[currentModalId] &&
            exchanges[currentModalId].result?.status !== ResultCode.success &&
            [StepNames.MATCHING, StepNames.MATCH_CREATED, StepNames.MATCH_EXECUTED].includes(
                exchanges[currentModalId].currentStep
            ) &&
            exchanges[currentModalId].result?.status === ResultCode.pending
        ) {
            toast((modalProps: any) => <TransactionStatusCard exchange={exchanges[currentModalId]} {...modalProps} />, {
                autoClose: 5000,
                position: isLargeScreen ? 'bottom-right' : 'bottom-center',
                toastId: currentModalId,
                pauseOnHover: true,
            })
        }

        // If swap completed successfully, track it and reset amounts
        if (exchanges[currentModalId]?.result?.status === ResultCode.success) {
            const exchange = exchanges[currentModalId]
            const sourceTxHash = exchange.result?.sourceTxHash || ''
            const destinationTxHash = exchange.result?.destinationTxHash || ''

            // Track when trade is successfully completed
            posthog.capture('trade_completed', {
                from_token: exchange.sellData.token.symbol,
                to_token: exchange.buyData.token.symbol,
                from_amount: exchange.sellData.value.toString(),
                actual_to_amount: exchange.buyData.value.toString(), // This could differ from expected
                src_transaction_hash: sourceTxHash,
                dst_transaction_hash: destinationTxHash,
                user_address:
                    mainWalletAddress || evmWalletAddress || solanaWalletAddress?.toString() || tronWalletAddress,
                chain: exchange.sellData.chainId,
                is_cross_chain: isCrossChain,
                liquidity_source: liquiditySource,
            })

            updateSellData({ value: BigInt(0) })
            updateBuyData({ value: BigInt(0) })
            setSellAmountString('') // Reset sell amount string
            resetStopwatch()

            // Invalidate balances to refresh after successful swap
            invalidateBalances()
        }

        // Reset states, close modal, etc.
        setWaitingForConnectedWalletInteraction(false)
        setIsReviewSwapModalOpen(false)
        removeHistory(exchangeId)
        refreshStatus()
    }

    useEffect(() => {
        // Re-sanitize sell string in case of token change, but only if we have a value
        if (sellData.value !== BigInt(0)) {
            setSellAmountString(
                formatNumberStringWithCommas(returnSanitizedSellAmount(sellAmountString, sellData.token.decimals)) ?? ''
            )
        }
    }, [sellData.token]) // Only run when token changes, not all sellData changes

    useEffect(() => {
        if (!sellData.token.native) {
            getAllowance(+sellData.chainId, sellData.token.address, mainWalletAddress || '', swapContract.address)
                .then(allowance => {
                    setSellAllowance(allowance)
                })
                .catch(e => {
                    logger.error(
                        `Cannot get allowance (chainId = ${+sellData.chainId}, token=${sellData.token.address},wallet=${mainWalletAddress || ''},swapContract=${swapContract.address})`,
                        e
                    )
                })
        }
    }, [sellData.token])

    const swapContract = useSwapContract(sellData, LiquiditySource.unavailable)

    const { onToggleExchangeTokens: onToggleExchangeTokensTokens } = useToggleExchangeTokens({
        isWrapOrUnwrapOnly: isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData),
        isBuyOutputLoading,
        updateBothData,
        sellData,
        buyData,
        setSellAmountString,
    })

    // Remove the checkBalancesAndAllowances function and replace with this version
    const checkBalancesAndAllowances = async (
        tokenAddress: string,
        amount: bigint,
        isNative: boolean
    ): Promise<{ hasBalance: boolean; hasAllowance?: boolean }> => {
        try {
            let relevantBalance: bigint

            if (isNative) {
                relevantBalance = nativeBalance?.bigintValue || BigInt(0)
            } else if (tokenAddress === WRAPPED_GAS_TOKENS[sellData.chainId]?.address) {
                relevantBalance = wrappedNativeBalance?.bigintValue || BigInt(0)
            } else {
                relevantBalance = sellData.token.userBalance || BigInt(0)
            }

            if (relevantBalance < amount) {
                return { hasBalance: false }
            }

            // Only check allowance for non-native tokens
            if (!isNative) {
                const allowance = await getAllowance(
                    +sellData.chainId,
                    tokenAddress,
                    mainWalletAddress || '',
                    swapContract.address
                )

                return {
                    hasBalance: true,
                    hasAllowance: BigInt(allowance) >= amount,
                }
            }

            // Native tokens don't need allowance
            return {
                hasBalance: true,
                hasAllowance: true,
            }
        } catch (error) {
            logger.error('Error checking balances and allowances:', error)
            return {
                hasBalance: false,
                hasAllowance: false,
            }
        }
    }

    // Update onConfirmExchange to use the new function
    const onConfirmExchange = async () => {
        let initialStep = StepNames.UNSTARTED

        try {
            if (isWrapOnly(sellData, buyData)) {
                initialStep = StepNames.DEPOSIT_WETH
            } else if (isUnWrapOnly(sellData, buyData)) {
                initialStep = StepNames.UNWRAP_WETH
            } else {
                if (!sellData.token?.address || !sellData.value) {
                    initialStep = StepNames.UNSTARTED
                } else {
                    const isNativeToken = sellData.token.address === NATIVE_TOKEN_ADDRESS[sellData.chainId]
                    const wrappedToken = WRAPPED_GAS_TOKENS[sellData.chainId]

                    logger.info(
                        `[onConfirmExchange]: isNativeToken=${isNativeToken}, wrappedToken=${wrappedToken.address}, nativeBalance=`,
                        nativeBalance,
                        `wrappedNativeBalance=`,
                        wrappedNativeBalance
                    )

                    if (isNativeToken && wrappedToken) {
                        // Check native token balance first
                        const _nativeBalance = nativeBalance?.bigintValue || BigInt(0)

                        // TODO: Low priority: the max amount that can be spent in this case is ETH balance + WETH balance - gas fees in ETH.
                        // For example if I have 0.1 ETH and 0.3 WETH, I can spend 0.4 ETH (minus gas fees), but my 0.1 ETH
                        // would need to be wrapped first.
                        // So in case of native assets, we could only wrap the diff, since the solver spends WETH only
                        // If WETH balance > sellData.value, then the wrap step can be skipped.

                        // The maximum amount that can be spent is
                        if (_nativeBalance < sellData.value) {
                            initialStep = StepNames.UNSTARTED
                        } else {
                            // Check wrapped token balance and allowance
                            const { hasBalance, hasAllowance } = await checkBalancesAndAllowances(
                                wrappedToken.address,
                                sellData.value,
                                false
                            )

                            logger.info('[onConfirmExchange]: hasBalance=', hasBalance, 'hasAllowance=', hasAllowance)

                            if (!hasBalance) {
                                initialStep = StepNames.DEPOSIT_WETH
                            } else {
                                initialStep = hasAllowance ? StepNames.CONFIRM_SWAP : StepNames.APPROVE_TOKENS
                            }
                        }
                    } else {
                        // For non-native tokens
                        const { hasBalance, hasAllowance } = await checkBalancesAndAllowances(
                            sellData.token.address,
                            sellData.value,
                            false
                        )

                        logger.info('[onConfirmExchange]: hasBalance=', hasBalance, 'hasAllowance=', hasAllowance)

                        if (!hasBalance) {
                            initialStep = StepNames.UNSTARTED
                        } else {
                            initialStep = hasAllowance ? StepNames.CONFIRM_SWAP : StepNames.APPROVE_TOKENS
                        }
                    }
                }
            }
        } catch (error) {
            logger.error('Error determining initial step:', error)
            initialStep = StepNames.UNSTARTED
        }

        const newExchangeId = uuidv4()

        logger.info(`[onConfirmExchange] Determined initial step:`, initialStep)

        updateHistory(newExchangeId, {
            buyData,
            sellData,
            currentStep: initialStep,
        })

        setExchangeId(newExchangeId)
        setCurrentModalId(newExchangeId)
        setIsReviewSwapModalOpen(true)

        // Track when trade is confirmed by user
        posthog.capture('trade_confirmed', {
            from_token: sellData.token.symbol,
            to_token: buyData.token.symbol,
            from_amount: sellData.value.toString(),
            expected_to_amount: buyData.value.toString(),
            user_address: mainWalletAddress || evmWalletAddress || solanaWalletAddress?.toString() || tronWalletAddress,
            chain: sellData.chainId,
            fees: feesDisplay,
            miles: miles,
            initial_step: initialStep,
            is_cross_chain: isCrossChain,
            liquidity_source: liquiditySource,
        })
    }

    const referrer = useMemo(() => searchParams.get('referrer') ?? undefined, [searchParams.toString()])

    // Add handler for copying buy amount
    const handleCopyBuyAmount = () => {
        const amount = formatExchangeAmountWithCommas(buyData)
        navigator.clipboard.writeText(amount)
        toast.success('Amount copied to clipboard!', {
            position: 'bottom-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        })
    }

    const exchangeTokenHandler = async (quote?: IQuoteData) => {
        try {
            await exchange(referrer, quote)
        } catch (err: any) {
            // Don't show error toast for user rejections
            if (err.message?.includes('User rejected') || err.message?.includes('user rejected') || err.code === 4001) {
                return
            }

            // Show toast for other errors
            const errorMessage = (err.shortMessage || err.message || 'Error occurred').split('\n')[0]

            toast.error(errorMessage, {
                position: 'bottom-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            })
        }
    }

    const onClickConfirmSwap = () => {
        exchangeTokenHandler(quoteResponse.quote ?? undefined)
        posthog.capture('onClickConfirmSwap', {
            wallet_address: evmWalletAddress,
        })
    }

    const toggleCustomWallet = () => {
        logger.debug('[toggleCustomWallet] isCustomWallet:', { isCustomWallet })

        const autoDetectedAddress = getAutoDetectedAddress(buyData.chainId)

        // Use Promise.all to ensure both state updates are complete
        Promise.all([
            new Promise<void>(resolve => {
                setDestinationAddress(autoDetectedAddress as Address)
                resolve()
            }),
            new Promise<void>(resolve => {
                setIsCustomWallet(prev => !prev)
                resolve()
            }),
        ]).then(() => {
            // Refetch quote after both state updates are complete
            refetchQuote()
        })
    }

    const { destinationAddress, setDestinationAddress } = useDestinationAddress({
        isCrossChain,
        buyData,
        sellData,
        evmWalletAddress,
        solanaWalletAddress,
        tronWalletAddress,
    })

    // Define a callback for successful swaps that refreshes balances
    const onSwapSuccess = async () => {
        // Refresh all balances and wait for them to update
        await refreshAllBalancesAndWait()
    }

    const { exchange } = useExchangeToken(exchangeId, liquiditySource, destinationAddress, onSwapSuccess)

    const onChangeDestAddress = (event: ChangeEvent<HTMLInputElement>) => {
        setDestinationAddress(event.target.value as Address)
    }

    useEffect(() => {
        if (isCrossChain) {
            // If we are going from EVM → Sol/Tron or Sol/Tron → EVM
            setIsCustomWallet(true)
        } else {
            // If we are not cross-chain (e.g. EVM → EVM, Sol → Sol),
            // then revert customWallet to false so it won't require a destination address
            setIsCustomWallet(false)
        }

        const detectedAddress = getAutoDetectedAddress(buyData.chainId)
        setDestinationAddress(detectedAddress as Address)
    }, [isCrossChain, buyData.chainId, evmWalletAddress, solanaWalletAddress, tronWalletAddress])

    // Add this wrapper function inside your component
    const updateBothDataWrapper = (data: { sell: Partial<IExchangeData>; buy: Partial<IExchangeData> }) => {
        updateBothData(data.sell, data.buy)
    }

    // Then use the wrapper in your effect
    useEffect(() => {
        validateUrlTokenParams(searchParams, updateBothDataWrapper)
    }, []) // Run once on mount

    // Also add runtime validation whenever tokens change
    useEffect(() => {
        // Skip if tokens aren't loaded yet
        if (!sellData.token || !buyData.token) return

        // Check for same token on same chain
        if (isSameExchangeToken(sellData, buyData)) {
            logger.info('Detected same token on both sides - resetting sell to default')

            // Reset sell to default
            updateSellData({
                token: DEFAULT_SELL_TOKEN,
                chainId: DEFAULT_SELL_TOKEN.chainId,
            })
        }
    }, [sellData.token?.address, buyData.token?.address, sellData.chainId, buyData.chainId])

    // Update your token selection handler to use the helper
    const onTokenSelectionChange = (newData: { token?: IToken; chainId?: number }, type: 'buy' | 'sell') => {
        handleTokenSelection(newData, type, sellData, buyData, updateSellData, updateBuyData)
    }

    // Update the cleanup effect
    useEffect(() => {
        const abortController = new AbortController()

        return () => {
            abortController.abort()
        }
    }, [])

    // Refetch quote when token selection changes
    useEffect(() => {
        // Skip if we're in the middle of a swap operation
        if (isToggleInProgress) {
            return
        }

        // Skip for wrap/unwrap only
        if (isWrapOrUnwrapOnly) {
            return
        }

        if (sellData.token?.address && buyData.token?.address && sellData.value !== BigInt(0)) {
            // Track when user starts the trade flow
            posthog.capture('trade_initiated', {
                from_token: sellData.token.symbol,
                to_token: buyData.token.symbol,
                from_amount: sellData.value.toString(),
                expected_to_amount: buyData.value.toString(),
                user_address:
                    mainWalletAddress || evmWalletAddress || solanaWalletAddress?.toString() || tronWalletAddress,
                chain: sellData.chainId,
            })

            setTimeout(() => {
                refetchQuote()
            }, 0)
        }
    }, [
        `${sellData.token?.address}-${sellData.token?.chainId}-${buyData.token?.address}-${buyData.token?.chainId}-${sellData.value}`,
        isToggleInProgress, // Add isToggleInProgress to dependencies
    ])

    // Replace the manual gas calculation with the hook
    const gasFeeString = useGasEstimation({
        sellData,
        sellAllowance,
        precomputedGasPrices,
        nativeTokenPricesInUSD,
        isCCTP,
        isWrapOrUnwrapOnly,
    })

    return (
        <>
            <div className='w-[464px] max-w-[calc(100%-24px)] space-y-2 rounded-2xl border border-mach-modal-border bg-mach-modal-background px-2 py-4'>
                <div className='flex items-center justify-between px-4'>
                    <span className='text-[18px] font-medium'>Swap</span>
                    <RefreshButton
                        disabled={
                            !isQuoteEnabled || !sellData.value || Number(sellData.value) === 0 || isWrapOrUnwrapOnly
                        }
                        isLoading={isQuoteEnabled && (isBuyOutputLoading || isManuallyRefreshing || isQuoteLoading)}
                        onClick={() => {
                            handleManualRefresh()
                        }}
                    />
                </div>
                <div className='space-y-2'>
                    <div className='space-y-[6px]'>
                        <FromSection
                            sellData={sellData}
                            buyData={buyData}
                            isCorrectWalletConnected={isCorrectWalletConnected}
                            isInsufficientBalance={isInsufficientBalance}
                            isComputingGas={isComputing}
                            quoteResponse={quoteResponse}
                            isGasPrecomputingDone={isGasPrecomputingDone}
                            sellAmountString={sellAmountString}
                            onChangeSellValue={onChangeSellValue}
                            onSetMax={setMax}
                            onTokenSelectionChange={onTokenSelectionChange}
                        />

                        <ToggleExchangeTokens onToggleExchangeTokens={onToggleExchangeTokensTokens} />

                        <ToSection
                            buyData={buyData}
                            sellData={sellData}
                            isCustomWallet={isCustomWallet}
                            isCrossChain={isCrossChain}
                            onToggleCustomWallet={toggleCustomWallet}
                            buyAmountString={formatExchangeAmountWithCommas(buyData)}
                            isBuyOutputLoading={isBuyOutputLoading}
                            isQuoteLoading={isQuoteLoading}
                            onTokenSelectionChange={onTokenSelectionChange}
                            onClickAmount={handleCopyBuyAmount}
                            destinationAddress={destinationAddress}
                            validateAddress={validateAddress}
                            onChangeDestAddress={onChangeDestAddress}
                        />
                    </div>

                    {buyData.value !== BigInt(0) && (
                        <GasFeePanel
                            isGasFeeExpanded={isGasFeeExpanded}
                            gasFeeString={gasFeeString}
                            onToggleGasFee={() => setIsGasFeeExpanded(!isGasFeeExpanded)}
                            detailedRowsProps={{
                                destinationAddress,
                                evmWalletAddress,
                                isCustomWallet,
                                shortenedAddress,
                                sellData,
                                buyData,
                                gasFeeString,
                                precomputedGasPrices,
                                liquiditySource,
                                machFee,
                                miles,
                            }}
                            isPriceFlipped={isPriceFlipped}
                            setIsPriceFlipped={setIsPriceFlipped}
                            sellData={sellData}
                            buyData={buyData}
                            isWrapOrUnwrapOnly={isWrapOrUnwrapOnly}
                            isBuyOutputLoading={isBuyOutputLoading}
                            isQuoteLoading={isQuoteLoading}
                        />
                    )}
                    {/* Exchange page swap button logic. */}
                    {((): JSX.Element[] => {
                        let elements: JSX.Element[] = []

                        if (!isCorrectWalletConnected) {
                            elements = [
                                <div className='space-y-2' key='connect-button'>
                                    <ConnectButton className='h-14' isEvmOnly={false} />
                                </div>,
                            ]
                        } else {
                            elements.push(
                                <SwapActionButton
                                    key='swapbutton'
                                    sellData={sellData}
                                    buyData={buyData}
                                    sellAmountString={sellAmountString}
                                    quoteResponse={quoteResponse}
                                    errors={{
                                        isInsufficientBalance,
                                        isQuoteInvalid,
                                        isBuyOutputLoading,
                                    }}
                                    hasInsufficientGas={hasInsufficientGas}
                                    isCustomWallet={isCustomWallet}
                                    destinationAddress={destinationAddress}
                                    onConfirmExchange={() => {
                                        void onConfirmExchange()
                                    }}
                                    gasFeeSimulationInProgress={isComputing}
                                    isQuoteLoading={isQuoteLoading}
                                />
                            )

                            // Add Faucet buttons if we're in testing mode
                            // if (isFeatureEnabled('USE_TESTNETS_ONLY')) {
                            //     // Check if we need Monad faucet
                            //     if (
                            //         sellData.chainId === ChainId.MonadTestnet ||
                            //         buyData.chainId === ChainId.MonadTestnet
                            //     ) {
                            //         elements.push(
                            //             <FaucetButton
                            //                 key='monad-faucet'
                            //                 walletAddress={mainWalletAddress}
                            //                 chainId={ChainId.MonadTestnet}
                            //                 className='mt-2 w-full'
                            //                 initialCollapsed={true}
                            //             />
                            //         )
                            //     }

                            //     // Check if we need Sepolia faucet
                            //     if (sellData.chainId === ChainId.Sepolia || buyData.chainId === ChainId.Sepolia) {
                            //         elements.push(
                            //             <FaucetButton
                            //                 key='sepolia-faucet'
                            //                 walletAddress={mainWalletAddress}
                            //                 chainId={ChainId.Sepolia}
                            //                 className='mt-2 w-full'
                            //                 initialCollapsed={true}
                            //             />
                            //         )
                            //     }

                            //     // Check if we need MegaETH faucet
                            //     if (
                            //         sellData.chainId === ChainId.MegaEthTestnet ||
                            //         buyData.chainId === ChainId.MegaEthTestnet
                            //     ) {
                            //         elements.push(
                            //             <FaucetButton
                            //                 key='megaeth-faucet'
                            //                 walletAddress={mainWalletAddress}
                            //                 chainId={ChainId.MegaEthTestnet}
                            //                 className='mt-2 w-full'
                            //                 initialCollapsed={true}
                            //             />
                            //         )
                            //     }
                            // }

                            if (miles > 0 && !hasErrors && !isFeatureEnabled('USE_TESTNETS_ONLY')) {
                                elements.push(<MilesTracker key='miles' miles={miles} />)
                            }
                        }

                        return elements
                    })()}
                </div>
            </div>
            {transactionData && currentModalId && (
                <SwapInProgressFlow
                    isOpen={isReviewSwapModalOpen}
                    onClose={onCloseReviewSwapModal}
                    onConfirm={onClickConfirmSwap}
                    fees={feesDisplay}
                    miles={miles}
                    destinationAddress={destinationAddress || mainWalletAddress}
                    liquiditySource={liquiditySource}
                    {...transactionData}
                />
            )}
            <AllTooltips />
        </>
    )
}

export default ExchangePage
